import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Building2,
  Plus,
  FolderKanban,
  FileText,
  Calendar,
  DollarSign,
  Loader2,
  ChevronRight,
  Clock,
  Star,
  FileSignature,
  Pencil,
  FileDown,
  History,
  Share2
} from "lucide-react";
import { ReviewRequestDialog } from "@/components/ReviewRequestDialog";
import { ConvertEstimateDialog } from "@/components/contracts/ConvertEstimateDialog";
import { CustomerContractsCard } from "@/components/contracts/CustomerContractsCard";
import { ProcurementListsCard } from "@/components/procurement/ProcurementListsCard";
import { FormFields, FormValues } from "@/components/FormFields";
import { FormHistoryDialog } from "@/components/FormHistoryDialog";
import { ProductionSheetsCard } from "@/components/production/ProductionSheetsCard";
import { SubcontractorQuotesLog } from "@/components/bids/SubcontractorQuotesLog";
import { FinancialBalanceLedgerPanel } from "@/components/billing/FinancialBalanceLedgerPanel";

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showFormPickerModal, setShowFormPickerModal] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [showFillFormModal, setShowFillFormModal] = useState(false);
  const [showViewSubmission, setShowViewSubmission] = useState(null);
  const [availableForms, setAvailableForms] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [customerSubmissions, setCustomerSubmissions] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [fillData, setFillData] = useState({});
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [attachProjectId, setAttachProjectId] = useState("");
  const [historyFor, setHistoryFor] = useState(null);

  const openSubmissionPdf = (sub) => window.open(`${API}/forms/submissions/${sub.submission_id}/pdf`, "_blank");

  const saveSubmissionToProject = async (sub) => {
    try {
      const r = await axios.post(`${API}/forms/submissions/${sub.submission_id}/save-to-project`, {});
      toast.success(`Saved "${r.data.name}" to project documents`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save PDF to project"); }
  };

  const toggleShare = async (sub) => {
    try {
      const r = await axios.put(`${API}/forms/submissions/${sub.submission_id}/share`, { share_to_portal: !sub.share_to_portal });
      toast.success(r.data.share_to_portal ? "Shared to customer portal — they'll be asked to confirm" : "Removed from customer portal");
      setShowViewSubmission(r.data);
      await refreshSubmissions();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const ConfirmationBadge = ({ sub }) => {
    if (!sub.share_to_portal) return null;
    const st = sub.confirmation?.status;
    const cls = st === "confirmed" ? "bg-green-100 text-green-700" : st === "changes_requested" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800";
    const label = st === "confirmed" ? `Confirmed by ${sub.confirmation.name}` : st === "changes_requested" ? "Changes requested" : "Awaiting customer";
    return <Badge className={`text-[10px] ${cls}`} data-testid={`confirmation-${sub.submission_id}`}><Share2 className="w-3 h-3 mr-1" />{label}</Badge>;
  };
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    address: "",
    start_date: "",
    end_date: ""
  });

  useEffect(() => {
    fetchCustomer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCustomer = async () => {
    try {
      const [custRes, formsRes, templatesRes, subsRes] = await Promise.all([
        axios.get(`${API}/customers/${customerId}`),
        axios.get(`${API}/forms`).catch(() => ({ data: [] })),
        axios.get(`${API}/form-templates/library`).catch(() => ({ data: [] })),
        axios.get(`${API}/forms/submissions/${customerId}`).catch(() => ({ data: [] })),
      ]);
      setCustomer(custRes.data);
      setAvailableForms(formsRes.data || []);
      setFormTemplates(templatesRes.data || []);
      setCustomerSubmissions(subsRes.data || []);
      setNewProject(prev => ({ ...prev, address: custRes.data.address || "" }));
    } catch (error) {
      toast.error("Failed to load customer");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };

  const openFormForCustomer = (form) => {
    setSelectedForm(form);
    // Auto-fill customer info
    const prefill = {};
    if (customer) {
      prefill.first_name = customer.first_name || "";
      prefill.last_name = customer.last_name || "";
      prefill.email = customer.email || "";
      prefill.phone = customer.phone || "";
      prefill.address = customer.address || "";
      prefill.customer_name = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
      prefill.site_address = customer.address || "";
    }
    setFillData(prefill);
    setEditingSubmission(null);
    setAttachProjectId("");
    setShowFormPickerModal(false);
    setShowFillFormModal(true);
  };

  const refreshSubmissions = async () => {
    const subsRes = await axios.get(`${API}/forms/submissions/${customerId}`).catch(() => ({ data: [] }));
    setCustomerSubmissions(subsRes.data || []);
  };

  const openEditSubmission = async (sub) => {
    let form = availableForms.find(f => f.form_id === sub.form_id);
    if (!form) {
      form = await axios.get(`${API}/forms/${sub.form_id}`).then(r => r.data).catch(() => null);
    }
    if (!form) {
      form = { form_id: sub.form_id, name: sub.form_name, fields: Object.entries(sub.data || {}).map(([k, v]) => Array.isArray(v)
        ? { id: k, label: k.replace(/_/g, " "), type: "repeater", sub_fields: Object.keys(v[0] || {}).map(sk => ({ id: sk, label: sk.replace(/_/g, " ") })) }
        : { id: k, label: k.replace(/_/g, " "), type: typeof v === "boolean" ? "checkbox" : String(v).length > 80 ? "textarea" : "text" }) };
    }
    setSelectedForm(form);
    setFillData({ ...(sub.data || {}) });
    setEditingSubmission(sub);
    setAttachProjectId(sub.project_id || "");
    setShowViewSubmission(null);
    setShowFillFormModal(true);
  };

  const handleAttachProject = async (sub, projectId) => {
    try {
      await axios.put(`${API}/forms/submissions/${sub.submission_id}`, { project_id: projectId || null });
      toast.success(projectId ? "Form attached to project" : "Form detached from project");
      await refreshSubmissions();
    } catch (err) {
      toast.error("Failed to update project link");
    }
  };

  const handleSubmitForm = async (sendSms, sendEmail) => {
    try {
      if (editingSubmission) {
        const res = await axios.put(`${API}/forms/submissions/${editingSubmission.submission_id}`, {
          data: fillData,
          project_id: attachProjectId || null,
        });
        if (res.status === 202) toast.info("Edit request sent to owner for approval");
        else toast.success("Form updated");
      } else {
        await axios.post(`${API}/forms/${selectedForm.form_id}/submit`, {
          data: fillData,
          customer_id: customer.lead_id,
          project_id: attachProjectId || null,
          send_sms: sendSms,
          send_email: sendEmail,
        });
        toast.success("Form saved to customer profile!");
      }
      setShowFillFormModal(false);
      setFillData({});
      setEditingSubmission(null);
      await refreshSubmissions();
    } catch (err) {
      toast.error(editingSubmission ? "Failed to update form" : "Failed to submit form");
    }
  };

  // Auto-save draft every 30 seconds
  React.useEffect(() => {
    if (!showFillFormModal || !selectedForm || editingSubmission) return;
    const timer = setInterval(async () => {
      try {
        await axios.put(`${API}/forms/${selectedForm.form_id}/draft`, {
          data: fillData,
          customer_id: customer?.lead_id
        });
      } catch (e) { /* silent */ }
    }, 30000);
    return () => clearInterval(timer);
  }, [showFillFormModal, selectedForm, fillData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    if ((newProject.address || "").trim().length < 3) {
      toast.error("Install address is required");
      return;
    }
    
    setCreatingProject(true);
    try {
      const response = await axios.post(`${API}/customers/${customerId}/projects`, newProject);
      toast.success("Project created!");
      setShowNewProjectModal(false);
      setNewProject({ name: "", description: "", address: customer?.address || "", start_date: "", end_date: "" });
      // Navigate to the new project
      navigate(`/customers/${customerId}/projects/${response.data.project_id}`);
    } catch (error) {
      toast.error("Failed to create project");
    } finally {
      setCreatingProject(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "qualified": return "bg-blue-100 text-blue-700";
      case "converted": return "bg-green-100 text-green-700";
      case "won": return "bg-emerald-100 text-emerald-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getProjectStatusColor = (status) => {
    switch (status) {
      case "planning": return "bg-slate-100 text-slate-700";
      case "in_progress": return "bg-blue-100 text-blue-700";
      case "on_hold": return "bg-amber-100 text-amber-700";
      case "completed": return "bg-green-100 text-green-700";
      case "cancelled": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Customer not found</p>
        <Button onClick={() => navigate("/customers")} className="mt-4">
          Back to Customers
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-indigo-100 text-indigo-700 font-medium text-lg">
                {getInitials(customer.first_name, customer.last_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {customer.first_name} {customer.last_name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getStatusColor(customer.status)}>
                  {customer.status}
                </Badge>
                {customer.company && (
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {customer.company}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button size="sm" onClick={() => navigate(`/estimates/new?lead_id=${customer.lead_id}`)} data-testid="create-estimate-btn">
            <FileText className="w-4 h-4 mr-1" /> New Estimate
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowFormPickerModal(true)} data-testid="add-form-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Form
          </Button>
          <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => setShowContractDialog(true)} data-testid="create-contract-btn">
            <FileSignature className="w-4 h-4 mr-1" /> Create Contract
          </Button>
          <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setShowReviewDialog(true)} data-testid="request-review-btn">
            <Star className="w-4 h-4 mr-1" /> Request Review
          </Button>
        </div>
      </div>
      <ReviewRequestDialog open={showReviewDialog} onOpenChange={setShowReviewDialog} customer={customer} />
      <ConvertEstimateDialog open={showContractDialog} onOpenChange={setShowContractDialog} leadId={customer.lead_id} />

      <FinancialBalanceLedgerPanel customerId={customer.lead_id} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Customer Info */}
        <div className="space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {customer.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${customer.email}`} className="text-indigo-600 hover:underline">
                    {customer.email}
                  </a>
                </div>
              )}
              {customer.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <a href={`tel:${customer.phone}`} className="text-indigo-600 hover:underline">
                    {customer.phone}
                  </a>
                </div>
              )}
              {customer.address && (
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <span className="text-slate-700">{customer.address}</span>
                </div>
              )}
              {customer.source && (
                <div className="flex items-start gap-3 text-sm" data-testid="customer-source">
                  <Star className="w-4 h-4 text-slate-400 mt-0.5" />
                  <span className="text-slate-700">
                    <span className="capitalize">Source: {String(customer.source).replace(/_/g, " ")}</span>
                    {customer.source_details && <span className="block text-xs text-slate-500 mt-0.5">{customer.source_details}</span>}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Estimates */}
          {customer.estimates && customer.estimates.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Estimates ({customer.estimates.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {customer.estimates.slice(0, 5).map(estimate => (
                  <div 
                    key={estimate.estimate_id}
                    className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/estimates/${estimate.estimate_id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium">{estimate.estimate_number}</p>
                      <p className="text-xs text-slate-500">{estimate.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">${estimate.total?.toLocaleString()}</p>
                      <Badge variant="outline" className="text-xs">{estimate.status}</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <CustomerContractsCard leadId={customer.lead_id} />
        </div>

        {/* Right Column - Projects */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FolderKanban className="w-5 h-5" />
                  Projects
                </CardTitle>
                <Button onClick={() => setShowNewProjectModal(true)} data-testid="add-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Project
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {customer.projects && customer.projects.length > 0 ? (
                <div className="space-y-3">
                  {customer.projects.map(project => (
                    <div
                      key={project.project_id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:shadow-sm transition-shadow cursor-pointer group"
                      onClick={() => navigate(`/customers/${customerId}/projects/${project.project_id}`)}
                      data-testid={`project-card-${project.project_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-3 h-12 rounded-full"
                          style={{ backgroundColor: project.color || "#6366f1" }}
                        />
                        <div>
                          <h3 className="font-medium text-slate-900">{project.name}</h3>
                          {project.description && (
                            <p className="text-sm text-slate-500 line-clamp-1">{project.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            {project.start_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(project.start_date)}
                              </span>
                            )}
                            {project.address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {project.address}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getProjectStatusColor(project.status)}>
                          {project.status?.replace("_", " ")}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FolderKanban className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No projects yet</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Create a project to manage schedules, files, and more
                  </p>
                  <Button 
                    className="mt-4" 
                    onClick={() => setShowNewProjectModal(true)}
                    data-testid="add-first-project-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Project
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <SubcontractorQuotesLog leadId={customerId} />

      {/* Customer Forms Directory */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" /> Forms ({customerSubmissions.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowFormPickerModal(true)}><Plus className="w-3 h-3 mr-1" /> Add Form</Button>
          </div>
        </CardHeader>
        <CardContent>
          {customerSubmissions.length > 0 ? (
            <div className="space-y-2">
              {customerSubmissions.map(sub => (
                <div key={sub.submission_id} className="flex items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => setShowViewSubmission(sub)} data-testid={`submission-${sub.submission_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-indigo-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{sub.form_name}</p>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-1 gap-y-0.5">
                        <span>{new Date(sub.created_at).toLocaleDateString()} by {sub.submitted_by_name}</span>
                        {sub.revision > 1 && <span className="text-amber-600">· Rev {sub.revision}</span>}
                        <ConfirmationBadge sub={sub} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <select
                      className="hidden sm:block text-xs border rounded-md px-2 py-1 bg-white max-w-[160px]"
                      value={sub.project_id || ""}
                      onChange={(e) => handleAttachProject(sub, e.target.value)}
                      data-testid={`attach-project-${sub.submission_id}`}
                    >
                      <option value="">No project</option>
                      {(customer.projects || []).map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                    </select>
                    {sub.project_name && (
                      <Badge className="sm:hidden text-[10px] bg-indigo-100 text-indigo-700 max-w-[100px] truncate">
                        <FolderKanban className="w-3 h-3 mr-1 shrink-0" />{sub.project_name}
                      </Badge>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditSubmission(sub)} data-testid={`edit-submission-${sub.submission_id}`} title="Edit form">
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No forms submitted yet. Click "Add Form" to get started.</p>
          )}
        </CardContent>
      </Card>

      <ProductionSheetsCard customerId={customerId} />

      {/* Material Procurement */}
      <ProcurementListsCard customerId={customerId} />

      {/* Form Picker Modal */}
      <Dialog open={showFormPickerModal} onOpenChange={setShowFormPickerModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Select a Form</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {availableForms.length > 0 && <p className="text-xs font-semibold text-slate-400 uppercase">Your Forms</p>}
            {availableForms.map(form => (
              <button key={form.form_id} onClick={() => openFormForCustomer(form)}
                className="w-full text-left p-3 border rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors">
                <p className="font-medium text-sm">{form.name}</p>
                <p className="text-xs text-slate-500">{form.fields?.length || 0} fields</p>
              </button>
            ))}
            {formTemplates.length > 0 && <p className="text-xs font-semibold text-slate-400 uppercase mt-3">Template Library</p>}
            {formTemplates.map(tpl => (
              <button key={tpl.template_id} onClick={async () => {
                const res = await axios.post(`${API}/form-templates/use/${tpl.template_id}`);
                openFormForCustomer(res.data);
              }} className="w-full text-left p-3 border rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{tpl.name}</p>
                  <Badge className="text-[10px] bg-green-100 text-green-700">Template</Badge>
                </div>
                <p className="text-xs text-slate-500">{tpl.description}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fill Form Modal */}
      <Dialog open={showFillFormModal} onOpenChange={(v) => {
        if (!v && Object.keys(fillData).length > 0) {
          if (window.confirm("Save as draft before closing?")) {
            axios.put(`${API}/forms/${selectedForm?.form_id}/draft`, { data: fillData, customer_id: customer?.lead_id }).catch(() => {});
            toast.success("Draft saved");
          }
        }
        setShowFillFormModal(v);
      }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          {selectedForm && (
            <>
              <DialogHeader><DialogTitle>{editingSubmission ? `Edit: ${selectedForm.name}` : selectedForm.name}</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <Label className="text-xs text-indigo-700">Attach to Project (optional)</Label>
                  <select className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-white" value={attachProjectId}
                    onChange={(e) => setAttachProjectId(e.target.value)} data-testid="fill-form-project-select">
                    <option value="">No project — customer profile only</option>
                    {(customer?.projects || []).map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                </div>
                <FormFields fields={selectedForm.fields || []} data={fillData} onChange={setFillData} />
              </div>
              <DialogFooter className="flex-col gap-2">
                {!editingSubmission && (
                  <div className="flex items-center gap-3 text-sm w-full">
                    <label className="flex items-center gap-1"><input type="checkbox" id="form-send-email" className="w-4 h-4 rounded" /> Email</label>
                    <label className="flex items-center gap-1"><input type="checkbox" id="form-send-sms" className="w-4 h-4 rounded" /> SMS</label>
                    <span className="text-xs text-slate-400 ml-auto">Welcome message</span>
                  </div>
                )}
                <div className="flex gap-2 w-full">
                  <Button variant="outline" className="flex-1" onClick={() => setShowFillFormModal(false)}>Cancel</Button>
                  <Button className="flex-1" data-testid="fill-form-save-btn" onClick={() => handleSubmitForm(document.getElementById("form-send-sms")?.checked, document.getElementById("form-send-email")?.checked)}>
                    {editingSubmission ? "Save Changes" : "Save to Profile"}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View Submission Modal */}
      <Dialog open={!!showViewSubmission} onOpenChange={(v) => { if (!v) setShowViewSubmission(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          {showViewSubmission && (
            <>
              <DialogHeader>
                <DialogTitle>{showViewSubmission.form_name}</DialogTitle>
                <p className="text-xs text-slate-500">
                  Submitted {new Date(showViewSubmission.created_at).toLocaleString()} by {showViewSubmission.submitted_by_name}
                  {showViewSubmission.revision > 1 && ` · Rev ${showViewSubmission.revision}, last edited by ${showViewSubmission.last_edited_by_name}`}
                </p>
                {showViewSubmission.project_name && (
                  <Badge className="w-fit bg-indigo-100 text-indigo-700 text-xs"><FolderKanban className="w-3 h-3 mr-1" />{showViewSubmission.project_name}</Badge>
                )}
                {showViewSubmission.share_to_portal && showViewSubmission.confirmation?.status === "changes_requested" && (
                  <p className="text-xs text-red-700 bg-red-50 rounded p-2 mt-1">Customer asked for changes: "{showViewSubmission.confirmation.comment}"</p>
                )}
                {!showViewSubmission.project_id && <p className="text-[11px] text-slate-400">Attach to a project to share with the customer or file the PDF.</p>}
              </DialogHeader>
              <div className="py-2">
                <FormValues fields={(availableForms.find(f => f.form_id === showViewSubmission.form_id) || {}).fields || []} data={showViewSubmission.data || {}} />
              </div>
              <DialogFooter className="flex-wrap gap-2 sm:gap-2">
                <Button variant="outline" size="sm" onClick={() => openSubmissionPdf(showViewSubmission)} data-testid="view-submission-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
                {showViewSubmission.project_id && <Button variant="outline" size="sm" onClick={() => saveSubmissionToProject(showViewSubmission)} data-testid="view-submission-save-project-btn"><FolderKanban className="w-4 h-4 mr-1" /> Save PDF to project</Button>}
                <Button variant="outline" size="sm" onClick={() => setHistoryFor(showViewSubmission.submission_id)} data-testid="view-submission-history-btn"><History className="w-4 h-4 mr-1" /> History{showViewSubmission.revision > 1 ? ` (${showViewSubmission.revision})` : ""}</Button>
                {showViewSubmission.project_id && (
                  <Button variant={showViewSubmission.share_to_portal ? "secondary" : "outline"} size="sm" onClick={() => toggleShare(showViewSubmission)} data-testid="view-submission-share-btn">
                    <Share2 className="w-4 h-4 mr-1" /> {showViewSubmission.share_to_portal ? "Shared to portal ✓" : "Share to portal"}
                  </Button>
                )}
                <Button size="sm" onClick={() => openEditSubmission(showViewSubmission)} data-testid="view-submission-edit-btn">
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New Project Modal */}
      <FormHistoryDialog submissionId={historyFor} open={!!historyFor} onOpenChange={(v) => { if (!v) setHistoryFor(null); }} />
      <Dialog open={showNewProjectModal} onOpenChange={setShowNewProjectModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-indigo-600" />
              New Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g., Kitchen Renovation"
                className="mt-1"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Brief description of the project..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="project-address">Install Address *</Label>
              <Input
                id="project-address"
                value={newProject.address}
                onChange={(e) => setNewProject({ ...newProject, address: e.target.value })}
                placeholder="123 Main St, City, Province"
                className="mt-1"
                required
                data-testid="project-address-input"
              />
              {customer?.address && newProject.address !== customer.address && (
                <button type="button" className="text-xs text-indigo-600 mt-1 hover:underline" onClick={() => setNewProject({ ...newProject, address: customer.address })}>Use customer address: {customer.address}</button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={newProject.start_date}
                  onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={newProject.end_date}
                  onChange={(e) => setNewProject({ ...newProject, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProjectModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateProject} 
              disabled={creatingProject || !newProject.name.trim()}
            >
              {creatingProject ? "Creating..." : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
